import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Clock,
  ChevronRight,
  Twitter,
  Linkedin,
  Link2,
  BookOpen,
  Tag,
  Newspaper,
  Check,
  Rocket,
  User,
} from "lucide-react";
import { useState, useEffect } from "react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import { BLOG_POSTS_FULL, getPostBySlug } from "../data/blogPosts";

/* ─────────────────────────────────────────
   Utilities
───────────────────────────────────────── */
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/* ─────────────────────────────────────────
   Reading progress bar
───────────────────────────────────────── */
function ReadingProgressBar() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const total = scrollHeight - clientHeight;
      setPct(total > 0 ? Math.min(100, (scrollTop / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-zinc-200">
      <div
        className="h-full bg-electric transition-[width] duration-100 ease-linear"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   Copy-link share button
───────────────────────────────────────── */
function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid="blog-share-button"
      className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:border-charcoal hover:text-charcoal transition-colors"
    >
      {copied ? (
        <><Check className="w-3.5 h-3.5 text-electric" /> Copied!</>
      ) : (
        <><Link2 className="w-3.5 h-3.5" /> Copy link</>
      )}
    </button>
  );
}

/* ─────────────────────────────────────────
   Table of contents
───────────────────────────────────────── */
function TableOfContents({ headings, activeId }) {
  if (!headings.length) return null;
  return (
    <div className="border border-zinc-200 bg-white">
      <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
        <BookOpen className="w-3.5 h-3.5 text-electric shrink-0" />
        <span className="text-xs font-bold text-charcoal uppercase tracking-widest">
          In this article
        </span>
      </div>
      <nav aria-label="Table of contents">
        <ul className="py-2">
          {headings.map((h) => (
            <li key={h.id}>
              <a
                href={`#${h.id}`}
                className={`block px-5 py-2 text-[13px] leading-snug transition-colors border-l-2 ${
                  activeId === h.id
                    ? "border-electric text-electric font-semibold"
                    : "border-transparent text-zinc-500 hover:text-charcoal hover:border-zinc-300"
                }`}
              >
                {h.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/* ─────────────────────────────────────────
   Sidebar
───────────────────────────────────────── */
function Sidebar({ post, url, headings }) {
  const [activeId, setActiveId] = useState(headings[0]?.id ?? "");

  useEffect(() => {
    if (!headings.length) return;
    const els = headings.map((h) => document.getElementById(h.id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-10% 0% -75% 0%" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headings.length]);

  const moreArticles = BLOG_POSTS_FULL.filter(
    (p) => p.slug !== post.slug
  ).slice(0, 3);

  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(post.title)}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

  return (
    <aside className="space-y-5 lg:sticky lg:top-28 lg:self-start">

      {/* Table of contents */}
      <TableOfContents headings={headings} activeId={activeId} />

      {/* Share */}
      <div className="border border-zinc-200 bg-white p-5">
        <p className="text-xs font-bold text-charcoal uppercase tracking-widest mb-3">
          Share article
        </p>
        <div className="flex flex-wrap gap-2">
          <CopyLinkButton url={url} />
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:border-charcoal hover:text-charcoal transition-colors"
          >
            <Twitter className="w-3.5 h-3.5" /> Twitter
          </a>
          <a
            href={linkedinUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:border-charcoal hover:text-charcoal transition-colors"
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn
          </a>
        </div>
      </div>

      {/* More articles */}
      <div className="border border-zinc-200 bg-white">
        <div className="px-5 py-3.5 border-b border-zinc-200 flex items-center gap-2">
          <Newspaper className="w-3.5 h-3.5 text-electric shrink-0" />
          <span className="text-xs font-bold text-charcoal uppercase tracking-widest">
            More articles
          </span>
        </div>
        <ul className="divide-y divide-zinc-100">
          {moreArticles.map((p) => (
            <li key={p.slug}>
              <Link
                to={`/blog/${p.slug}`}
                className="flex gap-3 p-4 group hover:bg-zinc-50 transition-colors"
              >
                <div className="w-14 h-14 shrink-0 overflow-hidden border border-zinc-200 bg-zinc-100">
                  <img
                    src={p.cover}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-electric block mb-0.5">
                    {p.category}
                  </span>
                  <p className="text-[13px] font-semibold text-charcoal leading-snug line-clamp-2 group-hover:text-electric transition-colors">
                    {p.title}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-zinc-400">
                    <Clock className="w-3 h-3" /> {p.readTime}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
        <div className="px-4 py-3 border-t border-zinc-100">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 text-xs font-semibold text-charcoal hover:text-electric transition-colors"
          >
            View all articles <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Newsletter */}
      <div className="bg-charcoal text-white p-5">
        <p className="text-overline text-electric mb-2">Newsletter</p>
        <h3 className="font-display font-bold text-lg leading-snug mb-2">
          Weekly ASO Playbook
        </h3>
        <p className="text-[13px] text-zinc-400 mb-4 leading-relaxed">
          One field note every week from the team shipping #1 rankings. No fluff.
        </p>
        <input
          type="email"
          placeholder="you@company.com"
          className="w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2.5 placeholder:text-zinc-500 focus:outline-none focus:border-electric mb-2"
        />
        <button className="w-full bg-electric text-white text-sm font-semibold py-2.5 hover:bg-[#42A5F5] transition-colors">
          Subscribe Free
        </button>
      </div>

    </aside>
  );
}

/* ─────────────────────────────────────────
   Inline CTA block (mid-article)
───────────────────────────────────────── */
function InlineCTA() {
  return (
    <div className="my-10 border-l-4 border-electric bg-zinc-50 border border-zinc-200 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
      <div className="w-10 h-10 bg-electric flex items-center justify-center shrink-0">
        <Rocket className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display font-bold text-charcoal text-lg leading-snug">
          Want results like these for your app?
        </p>
        <p className="text-sm text-zinc-600 mt-1">
          Get a free 47-point ASO audit from the team behind 100+ #1 rankings.
        </p>
      </div>
      <Link
        to="/free-aso-audit"
        className="shrink-0 inline-flex items-center gap-2 bg-electric text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#42A5F5] transition-colors whitespace-nowrap"
      >
        Get Free Audit <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────
   Author card
───────────────────────────────────────── */
function AuthorCard({ author }) {
  const parts = author.split("·").map((s) => s.trim());
  const name = parts[0];
  const role = parts[1] ?? "";
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
  return (
    <div className="mt-12 p-6 border border-zinc-200 bg-zinc-50 flex gap-5 items-start">
      <div className="w-14 h-14 shrink-0 bg-charcoal border border-zinc-300 flex items-center justify-center">
        <span className="font-display font-extrabold text-lg text-white tracking-tight">
          {initials}
        </span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-3.5 h-3.5 text-electric" />
          <span className="text-xs font-bold uppercase tracking-widest text-electric">
            Written by
          </span>
        </div>
        <p className="font-display font-bold text-charcoal text-base">{name}</p>
        {role && (
          <p className="text-sm text-zinc-500 mt-0.5">{role}</p>
        )}
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
          Part of Lumotis's growth team. Has shipped top-3 rankings for
          apps across 15+ markets in the last two years.
        </p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   Main page
───────────────────────────────────────── */
export default function BlogDetailPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <>
        <Navbar />
        <main className="pt-40 pb-40 text-center">
          <p className="text-overline text-electric mb-3">404</p>
          <h1 className="font-display text-4xl font-extrabold text-charcoal mb-6">
            Post not found
          </h1>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold hover:bg-[#42A5F5] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const url = `https://lumotis.com/blog/${post.slug}`;

  /* Pre-compute headings with stable IDs */
  let h2Idx = 0;
  const contentBlocks = post.content.map((block) => {
    if (block.type === "h2") {
      return { ...block, id: `section-${h2Idx++}` };
    }
    return block;
  });
  const headings = contentBlocks
    .filter((b) => b.type === "h2")
    .map((b) => ({ id: b.id, text: b.text }));

  /* Insert inline CTA after ~half the content */
  const ctaInsertAfter = Math.ceil(contentBlocks.length / 2);
  const blocksWithCta = [
    ...contentBlocks.slice(0, ctaInsertAfter),
    { type: "inline-cta" },
    ...contentBlocks.slice(ctaInsertAfter),
  ];

  const related = BLOG_POSTS_FULL.filter((p) => p.slug !== post.slug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.cover,
    datePublished: post.publishedAt,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: "Lumotis",
      logo: { "@type": "ImageObject", url: "https://lumotis.com/logo.png" },
    },
    description: post.excerpt,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <>
      <Helmet>
        <title>{`${post.title} | Lumotis Blog`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={post.cover} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <ReadingProgressBar />
      <Navbar />

      <main data-testid="blog-detail-page">

        {/* ══════════════════════════════════════
            HERO — full-width, no sidebar column
        ══════════════════════════════════════ */}
        <div className="bg-white pt-28 md:pt-32 pb-10 md:pb-14 border-b border-zinc-200">
          <div className="container max-w-4xl">

            {/* Breadcrumb */}
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-400 mb-8"
            >
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-electric transition-colors">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-zinc-600">{post.category}</span>
            </nav>

            {/* Category tag */}
            <span className="inline-flex items-center gap-1.5 border border-electric text-electric text-[11px] font-bold px-3 py-1 uppercase tracking-widest mb-6">
              <Tag className="w-3 h-3" /> {post.category}
            </span>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="font-display text-4xl md:text-5xl lg:text-[56px] font-extrabold text-charcoal leading-[1.05] tracking-tight"
              data-testid="blog-detail-title"
            >
              {post.title}
            </motion.h1>

            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 }}
              className="mt-5 text-lg md:text-xl text-zinc-500 leading-relaxed"
            >
              {post.excerpt}
            </motion.p>

            {/* Meta row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.45, delay: 0.2 }}
              className="mt-8 pt-6 border-t border-zinc-200 flex flex-wrap items-center gap-x-6 gap-y-3"
            >
              <span className="flex items-center gap-2 text-sm text-zinc-500">
                <div className="w-7 h-7 bg-charcoal flex items-center justify-center shrink-0">
                  <span className="text-white text-[10px] font-bold">
                    {post.author.charAt(0)}
                  </span>
                </div>
                <span className="font-medium text-charcoal">{post.author.split("·")[0].trim()}</span>
              </span>
              <span className="flex items-center gap-1.5 text-sm text-zinc-400">
                <Calendar className="w-3.5 h-3.5" /> {formatDate(post.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5 text-sm text-zinc-400">
                <Clock className="w-3.5 h-3.5" /> {post.readTime}
              </span>
            </motion.div>

          </div>
        </div>

        {/* ══════════════════════════════════════
            COVER IMAGE — full container width
        ══════════════════════════════════════ */}
        <div className="bg-white border-b border-zinc-200">
          <div className="container max-w-7xl py-8 md:py-10">
            <div className="w-full aspect-[21/9] overflow-hidden bg-zinc-100 border border-zinc-200">
              <img
                src={post.cover}
                alt={post.title}
                className="w-full h-full object-cover"
                loading="eager"
                width="1200"
                height="514"
              />
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════
            BODY + SIDEBAR — two-column
        ══════════════════════════════════════ */}
        <section className="bg-white py-14 md:py-20">
          <div className="container max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-12 xl:gap-16 items-start">

              {/* ── Article body ── */}
              <article>

                {blocksWithCta.map((block, idx) => {
                  if (block.type === "inline-cta") {
                    return <InlineCTA key="inline-cta" />;
                  }
                  if (block.type === "h2") {
                    return (
                      <h2
                        key={idx}
                        id={block.id}
                        className="font-display text-2xl md:text-[28px] font-extrabold text-charcoal mt-14 mb-5 leading-tight scroll-mt-32 border-l-4 border-electric pl-4"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  return (
                    <p
                      key={idx}
                      className="text-[17px] text-zinc-700 leading-[1.9] mb-6"
                    >
                      {block.text}
                    </p>
                  );
                })}

                {/* Author */}
                <AuthorCard author={post.author} />

                {/* Article footer */}
                <div className="mt-10 pt-8 border-t border-zinc-200 flex flex-wrap items-center justify-between gap-4">
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-charcoal transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back to all posts
                  </Link>
                  <Link
                    to="/free-aso-audit"
                    className="inline-flex items-center gap-2 bg-charcoal text-white px-6 py-3 text-sm font-semibold hover:bg-zinc-700 transition-colors"
                  >
                    Get free ASO audit <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

              </article>

              {/* ── Sidebar ── */}
              <Sidebar post={post} url={url} headings={headings} />

            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            RELATED POSTS
        ══════════════════════════════════════ */}
        <section className="py-16 md:py-24 bg-zinc-50 border-t border-zinc-200">
          <div className="container max-w-7xl">

            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-overline text-electric mb-2">Continue reading</p>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-charcoal">
                  Keep going
                </h2>
              </div>
              <Link
                to="/blog"
                className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal hover:text-electric transition-colors border border-zinc-300 hover:border-charcoal px-4 py-2"
              >
                All articles <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p, idx) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.07 }}
                  className="h-full"
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group border border-zinc-200 bg-white hover:border-charcoal transition-colors flex flex-col h-full"
                  >
                    <div className="aspect-[16/10] overflow-hidden border-b border-zinc-200 bg-zinc-100">
                      <img
                        src={p.cover}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        loading="lazy"
                        width="600"
                        height="375"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-overline text-electric">{p.category}</span>
                        <span className="w-1 h-1 rounded-full bg-zinc-300" />
                        <span className="text-xs text-zinc-400">{formatDate(p.publishedAt)}</span>
                      </div>
                      <h3 className="font-display text-lg font-bold text-charcoal leading-snug flex-1">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-zinc-500 line-clamp-2 leading-relaxed">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 pt-4 border-t border-zinc-100 flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs text-zinc-400">
                          <Clock className="w-3.5 h-3.5" /> {p.readTime}
                        </span>
                        <span className="flex items-center gap-1 text-xs font-bold text-charcoal group-hover:text-electric transition-colors">
                          Read <ArrowUpRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

          </div>
        </section>

        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
